import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CreditCard, Star, Trash2, Plus, Loader2 } from "lucide-react";

/**
 * Faz 2A — Ayarlar > Ödeme Yöntemi
 *
 * Kart bilgisi (PAN/CVV/expiry) hiçbir yerde ekranda görünmez.
 * PayTR tarafında saklanan tokenlar Penz veritabanında maskeli olarak
 * (brand + last4) tutulur ve kullanıcıya "Visa •••• 4821" biçiminde
 * gösterilir.  Yeni kart ekleme akışı iFrame'e yönlendirir (mevcut
 * abonelik ödeme akışı — bir sonraki tahsilat kartı kaydeder).
 */
export default function PaymentMethodsCard() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment-methods");
      setMethods(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ödeme yöntemleri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSetDefault = async (id) => {
    setBusyId(id);
    try {
      await api.patch(`/payment-methods/${id}/default`);
      toast.success("Varsayılan kart güncellendi");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/payment-methods/${id}`);
      toast.success("Kart kaldırıldı");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kart silinemedi");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SectionCard
      title="Ödeme Yöntemi"
      action={
        <Button
          size="sm"
          onClick={() => (window.location.href = "/abonelik?add-card=1")}
          data-testid="add-payment-method-button"
        >
          <Plus className="w-4 h-4 mr-1" />
          Yeni Kart Ekle
        </Button>
      }
    >
      <div className="p-5 space-y-3" data-testid="payment-methods-list">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
          </div>
        ) : (methods ?? []).length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-lg">
            Henüz kayıtlı kartınız yok. Aboneliği başlatırken kartınız
            otomatik olarak güvenli şekilde kaydedilir.
          </div>
        ) : (
          (methods ?? []).map((pm) => (
            <div
              key={pm.id}
              className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-white"
              data-testid={`payment-method-${pm.id}`}
            >
              <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                <CreditCard className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                  {pm.display}
                  {pm.is_default && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Star className="w-3 h-3" /> Varsayılan
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  PayTR tarafından güvenli saklanır — kart numarası Penz'de tutulmaz.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!pm.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyId === pm.id}
                    onClick={() => onSetDefault(pm.id)}
                    data-testid={`set-default-${pm.id}`}
                  >
                    Varsayılan Yap
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === pm.id}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      data-testid={`delete-payment-${pm.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Kartı Kaldır</AlertDialogTitle>
                      <AlertDialogDescription>
                        {pm.display} kartı ödeme yöntemi olarak kaldırılacak.
                        Sonraki abonelik yenilemeleri artık bu kart ile
                        yapılmayacak. Aktif aboneliğinizin son kartı ise
                        silinemez; önce yeni bir kart eklemeli veya
                        aboneliği iptal etmelisiniz.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-rose-600 hover:bg-rose-700"
                        onClick={() => onDelete(pm.id)}
                      >
                        Kartı Sil
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))
        )}
      </div>
    </SectionCard>
  );
}
